import React, { createContext, FC, useEffect, useState, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ampGlobalProps, useAmplitude } from 'src/lib/amplitude';
import { useErrorBoundary } from 'src/shared/components/ErrorBoundary';
import { appInsights } from 'src/shared/lib/appInsights';
import { useBackend } from 'src/components/provider/BackendProvider';
import { useFlags } from 'src/shared/components/LaunchDarkly';
import { getDeeplinkCode, getJoinToken, getPlatformType } from 'src/components/utils/Helper';
import { formatLocalUrlTemplate } from 'src/lib/utils/stringUtils';

enum LinkSource {
  Email = 'email',
  Sms = 'SMS',
  Unknown = 'unknown',
}

export const getSource = (): LinkSource => {
  const params = new URL(window.location.href).searchParams;
  // See https://athenajira.athenahealth.com/browse/MESACT-777 for SMS click tracking
  if (!params.get('E')) {
    return LinkSource.Email;
  }
  if (params.get('E') === 'L') {
    return LinkSource.Sms;
  }

  return LinkSource.Unknown;
};

export interface AppointmentState {
  providerName: string;
  start: string;
  joinToken: string;
  appointmentId: number;
  apptLoading: boolean;
  invalidAppt: boolean;
  brandName: string;
  medicalGroupName: string;
  loggingUrl: string;
  departmentId: number;
  providerId: number;
  setJoinToken: React.Dispatch<React.SetStateAction<string>>;
  validateDeeplinkCode: () => Promise<DeeplinkDetails | null>;
}

export const AppointmentStateContext = createContext<AppointmentState | null>(null);

const AppointmentProvider: FC<WithChildren> = ({ children }) => {
  const {
    flags: { optumTermsAndConditions },
    initializeClient,
  } = useFlags();
  const { appointmentApi, brandNameApi, deeplinkApi, medicalGroupApi } = useBackend();
  const [apptDetails, setApptDetails] = useState<AppointmentDetails>({} as AppointmentDetails);
  const [brandName, setBrandName] = useState('');
  const [medicalGroupName, setMedicalGroupName] = useState<string>('');
  const [apptLoading, setApptLoading] = useState(true);
  const [invalidAppt, setInvalidAppt] = useState(false);
  const [joinToken, setJoinToken] = useState<string>(getJoinToken());
  const { throwError } = useErrorBoundary();
  const { i18n } = useTranslation();
  const language = i18n.language.toUpperCase();
  const { setContextId, setUserProperties, setProductName } = useAmplitude();

  const validateAppointment = async (): Promise<boolean> => {
    try {
      const contextId = joinToken.split('.')[1];

      // Since Spark Amplitude now requires a contextId, this is where it is
      // first set and amplitude is initialized.
      setContextId(contextId);
      setProductName('THWebApp');

      ampGlobalProps['Common::DefaultLanguage'] = language;
      ampGlobalProps['Common::ContextID'] = contextId;
      ampGlobalProps['Common::DeviceType'] = getPlatformType();
      ampGlobalProps['Common::Source'] = getSource();
      ampGlobalProps['Common::EnvironmentKey'] = process.env.ENVIRONMENT || 'dev';

      setApptLoading(true);
      const data = await appointmentApi(joinToken);

      if (data.status === 200) {
        const json = (await data.json()) as AppointmentDetails;
        const loggingEndpoint = formatLocalUrlTemplate(json.loggingUrl);
        appInsights.addIgnoredTarget(loggingEndpoint);
        appInsights.customTelemetryTags['ai.operation.name'] = `th-patient/${contextId}/${json.appointmentId}`;
        // casting to string here places these values in customDimensions instead of customMeasurements
        appInsights.customTelemetryData['appointmentId'] = String(json.appointmentId);
        appInsights.customTelemetryData['departmentId'] = String(json.departmentId);
        appInsights.customTelemetryData['providerId'] = String(json.providerId);

        setUserProperties({ appointmentId: json.appointmentId });

        ampGlobalProps['Common::ApptID'] = String(json.appointmentId);

        setApptDetails(json);
        setApptLoading(false);

        // re-initialize LD again now that departmentId is available
        initializeClient({ contextId: parseInt(contextId), departmentId: json.departmentId });

        return true;
      }

      setInvalidAppt(true);
    } catch (error) {
      throwError(new Error('Failed API appointment'));
    }
    return false;
  };

  const fetchBrandName = async () => {
    // wrap in try catch to avoid console error
    try {
      setBrandName(await brandNameApi(joinToken));
    } catch {
      setBrandName('');
    }
  };

  const fetchMedicalGroupName = async () => {
    // wrap in try catch to avoid console error
    try {
      const { medicalGroupName } = await medicalGroupApi(joinToken);
      setMedicalGroupName(medicalGroupName);
    } catch {
      setMedicalGroupName('');
    }
  };

  const validateDeeplinkCode = async (): Promise<DeeplinkDetails | null> => {
    try {
      const data = await deeplinkApi(getDeeplinkCode());
      if (data.status === 200) {
        const json = (await data.json()) as DeeplinkDetails;
        return json;
      }
      setInvalidAppt(true);
    } catch (error) {
      throwError(new Error('Failed API deeplink'));
    }
    return null;
  };

  useEffect(() => {
    if (joinToken) {
      validateAppointment();
      fetchBrandName();
      if (optumTermsAndConditions) {
        // currently medical group name is only used for custom optum consent content
        fetchMedicalGroupName();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinToken]);

  const value: AppointmentState = useMemo(
    () => ({
      ...apptDetails,
      apptLoading,
      invalidAppt,
      joinToken,
      brandName,
      medicalGroupName,
      setJoinToken,
      validateDeeplinkCode,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apptDetails, apptLoading, invalidAppt, brandName, medicalGroupName]
  );

  return <AppointmentStateContext.Provider value={value}>{children}</AppointmentStateContext.Provider>;
};

const noopState: AppointmentState = {
  providerName: '',
  start: '',
  joinToken: '',
  appointmentId: 0,
  apptLoading: false,
  invalidAppt: false,
  brandName: '',
  medicalGroupName: '',
  loggingUrl: '',
  departmentId: 0,
  providerId: 0,
  setJoinToken: () => null,
  validateDeeplinkCode: async () => null,
};

const useAppointmentState = (): AppointmentState => {
  const state = useContext(AppointmentStateContext) || noopState;
  return state;
};

export { useAppointmentState };

export default AppointmentProvider;
